import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { Search, Loader2, MapPin, Radio, Handshake, AlertTriangle, X, Clock, Trash2, History, Navigation } from 'lucide-react';

import { getRecentSites, clearRecentSites } from '../../lib/recentSites';

const KIND_META = {
  site:        { label: 'Sites',         icon: MapPin,        color: 'text-[#A3FF12]' },
  node:        { label: 'Nodos KMZ',     icon: Radio,         color: 'text-orange-300' },
  negotiation: { label: 'Negociaciones', icon: Handshake,     color: 'text-blue-300' },
  incident:    { label: 'Incidencias',   icon: AlertTriangle, color: 'text-red-300' },
};

const OP_CHIPS = [
  { key: 'ORANGE',      label: 'ORANGE', color: '#ff6b00' },
  { key: 'WILLIAM_VDF', label: 'VDF',    color: '#e60000' },
  { key: 'WILLIAM_OSP', label: 'OSP',    color: '#3b82f6' },
  { key: 'AVE_OSP',     label: 'AVE',    color: '#10b981' },
];

const useDebounce = (v, ms = 250) => {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
};

/**
 * Global search dropdown that queries sites + negotiations + incidents
 * via `/api/search?q=...` and navigates on click.
 */
const GlobalSearch = ({ onNavigate }) => {
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [history, setHistory] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('sf_search_history') || '[]');
      return Array.isArray(raw) ? raw.slice(0, 5) : [];
    } catch { return []; }
  });
  const [recentSites, setRecentSites] = useState(() => getRecentSites());
  const [opFilter, setOpFilter] = useState(null); // null | 'ORANGE' | ...
  const [addresses, setAddresses] = useState([]); // Nominatim suggestions

  // Refresh recent-sites when SiteMasterModal broadcasts an update.
  useEffect(() => {
    const onChange = () => setRecentSites(getRecentSites());
    window.addEventListener('sf:recent-sites-changed', onChange);
    return () => window.removeEventListener('sf:recent-sites-changed', onChange);
  }, []);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const nav = useNavigate();
  const debounced = useDebounce(q, 220);

  // Cmd/Ctrl+K to focus
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Fetch on debounced change
  useEffect(() => {
    let cancel = false;
    const run = async () => {
      if (!debounced || debounced.trim().length < 2) { setData(null); setAddresses([]); return; }
      setLoading(true);
      try {
        const params = { q: debounced, limit: 6 };
        if (opFilter) params.operator = opFilter;
        const [{ data }, geoRes] = await Promise.all([
          api.get('/search', { params }),
          debounced.trim().length >= 3
            ? api.get('/geocode/suggest', { params: { q: debounced, limit: 5 } }).catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] }),
        ]);
        if (!cancel) {
          setData(data);
          setAddresses(geoRes.data || []);
          setHighlight(0);
        }
      } catch { if (!cancel) { setData(null); setAddresses([]); } }
      finally { if (!cancel) setLoading(false); }
    };
    run();
    return () => { cancel = true; };
  }, [debounced, opFilter]);

  const flat = React.useMemo(() => {
    if (!data) return [];
    return [
      ...(data.sites || []).map((x) => ({ ...x, group: 'site' })),
      ...(data.nodes || []).map((x) => ({ ...x, group: 'node' })),
      ...(data.negotiations || []).map((x) => ({ ...x, group: 'negotiation' })),
      ...(data.incidents || []).map((x) => ({ ...x, group: 'incident' })),
    ];
  }, [data]);

  const jump = (r) => {
    // Store in history (last 5, deduped by title)
    try {
      const key = 'sf_search_history';
      const prev = JSON.parse(localStorage.getItem(key) || '[]');
      const entry = { q, title: r.title, subtitle: r.subtitle, kind: r.kind, id: r.id, meta: r.meta, route: r.route, at: Date.now() };
      const dedup = [entry, ...prev.filter((x) => !(x.kind === r.kind && x.id === r.id))].slice(0, 5);
      localStorage.setItem(key, JSON.stringify(dedup));
      setHistory(dedup);
    } catch { /* localStorage disabled */ }

    // Pass query + focus id. For sites/nodes, also pass coords + code so the map can fly there and open the modal.
    const params = new URLSearchParams({ q, focus: r.id });
    if ((r.kind === 'site' || r.kind === 'node') && r.meta?.lat != null && r.meta?.lng != null) {
      params.set('lat', String(r.meta.lat));
      params.set('lng', String(r.meta.lng));
      if (r.meta.code) params.set('code', r.meta.code);
    }
    nav(`${r.route}?${params.toString()}`);
    setOpen(false);
    setQ('');
    onNavigate?.();
  };

  const jumpAddress = (addr) => {
    // Navega a Prospección con las coords para que el mapa vuele y análisis quede disponible
    const params = new URLSearchParams({
      lat: String(addr.lat), lng: String(addr.lng),
      address: addr.display_name,
    });
    nav(`/app/prospeccion?${params.toString()}`);
    setOpen(false);
    setQ('');
    onNavigate?.();
  };

  const onKeyDown = (e) => {
    if (!open || flat.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (flat[highlight]) jump(flat[highlight]); }
  };

  const showEmpty = q.trim().length >= 2 && !loading && data && flat.length === 0 && addresses.length === 0;

  return (
    <div ref={rootRef} className="relative px-3 pt-3">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"/>
        <input
          ref={inputRef}
          type="search"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Buscar sites, negocios, incidencias…"
          data-testid="global-search-input"
          className="w-full bg-black/60 border border-white/10 focus:border-[#A3FF12]/60 text-white text-xs rounded-lg pl-9 pr-14 py-2 focus:outline-none placeholder:text-white/30"
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading && <Loader2 size={12} className="animate-spin text-[#A3FF12]"/>}
          {q && !loading && (
            <button onClick={() => { setQ(''); setData(null); }} className="text-white/40 hover:text-white"><X size={12}/></button>
          )}
          {!q && !loading && (
            <kbd className="hidden lg:inline text-[9px] font-bold text-white/40 border border-white/10 rounded px-1 py-0.5">⌘K</kbd>
          )}
        </span>
      </div>

      {open && q.trim().length < 2 && (recentSites.length > 0 || history.length > 0) && (
        <div data-testid="global-search-history"
          className="absolute left-3 right-3 top-full mt-1 bg-[#0d0d0d] border border-white/15 rounded-xl shadow-2xl max-h-[70vh] overflow-y-auto z-[100]">

          {recentSites.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-white/40 flex items-center justify-between sticky top-0 bg-[#0d0d0d]">
                <span className="flex items-center gap-2"><History size={11} className="text-[#A3FF12]"/> Sites recientes · {recentSites.length}</span>
                <button onClick={() => { clearRecentSites(); }}
                  data-testid="btn-clear-recent-sites"
                  className="text-white/40 hover:text-red-400 flex items-center gap-1 normal-case tracking-normal font-normal text-[10px]">
                  <Trash2 size={10}/> Borrar
                </button>
              </div>
              {recentSites.map((s, i) => (
                <button key={`rs-${s.code}-${i}`} onClick={() => jump({
                  kind: 'site', id: s.code, title: s.title, subtitle: s.subtitle,
                  route: '/app/prospeccion', meta: { lat: s.lat, lng: s.lng, code: s.code },
                })} data-testid={`gs-recent-site-${i}`}
                  className="w-full text-left px-3 py-2 flex items-start gap-2.5 border-t border-white/5 hover:bg-[#A3FF12]/10 group">
                  <MapPin size={13} className="text-[#A3FF12] mt-0.5 flex-shrink-0"/>
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-xs font-bold truncate">{s.title}</div>
                    <div className="text-white/50 text-[10px] truncate">{s.subtitle || '—'}</div>
                  </div>
                  <span className="text-[#A3FF12] text-[10px] flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity font-bold">
                    Abrir en mapa →
                  </span>
                </button>
              ))}
            </>
          )}

          {history.length > 0 && (
            <>
              <div className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-white/40 flex items-center justify-between ${recentSites.length > 0 ? 'border-t border-white/10' : ''}`}>
                <span className="flex items-center gap-2"><Clock size={11}/> Búsquedas recientes · {history.length}</span>
                <button onClick={() => { localStorage.removeItem('sf_search_history'); setHistory([]); }}
                  data-testid="btn-clear-history"
                  className="text-white/40 hover:text-red-400 flex items-center gap-1 normal-case tracking-normal font-normal text-[10px]">
                  <Trash2 size={10}/> Borrar
                </button>
              </div>
              {history.map((h, i) => {
                const meta = KIND_META[h.kind] || KIND_META.site;
                const Icon = meta.icon;
                return (
                  <button key={`${h.kind}-${h.id}-${i}`} onClick={() => jump(h)} data-testid={`gs-history-${i}`}
                    className="w-full text-left px-3 py-2 flex items-start gap-2.5 border-t border-white/5 hover:bg-white/5">
                    <Icon size={13} className={`${meta.color} mt-0.5 flex-shrink-0`}/>
                    <div className="min-w-0 flex-1">
                      <div className="text-white text-xs font-bold truncate">{h.title}</div>
                      <div className="text-white/50 text-[10px] truncate">{h.subtitle}</div>
                    </div>
                    <span className="text-white/30 text-[10px] flex-shrink-0">
                      {new Date(h.at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                    </span>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}

      {open && q.trim().length >= 2 && (
        <div data-testid="global-search-dropdown"
          className="absolute left-3 right-3 top-full mt-1 bg-[#0d0d0d] border border-white/15 rounded-xl shadow-2xl max-h-[70vh] overflow-y-auto z-[100]">
          <div className="px-3 py-2 flex items-center gap-1.5 flex-wrap border-b border-white/10 sticky top-0 bg-[#0d0d0d] z-[1]">
            <span className="text-[10px] font-black uppercase tracking-widest text-white/40 mr-1">Operador:</span>
            <button onClick={() => setOpFilter(null)} data-testid="gs-op-all"
              className={`text-[10px] px-2 py-0.5 rounded-full font-bold border transition-colors ${
                opFilter === null ? 'bg-white/10 text-white border-white/30' : 'text-white/50 border-white/10 hover:text-white'
              }`}>Todos</button>
            {OP_CHIPS.map((c) => {
              const active = opFilter === c.key;
              return (
                <button key={c.key} onClick={() => setOpFilter(active ? null : c.key)}
                  data-testid={`gs-op-${c.label.toLowerCase()}`}
                  className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold border transition-colors ${
                    active ? 'bg-white/10 text-white border-white/30 ring-1' : 'text-white/50 border-white/10 hover:text-white'
                  }`}
                  style={active ? { boxShadow: `0 0 0 1px ${c.color}55` } : {}}>
                  <span style={{ background: c.color, width: 6, height: 6, borderRadius: '50%', display: 'inline-block' }}/>
                  {c.label}
                </button>
              );
            })}
            {opFilter && (
              <span className="text-[9px] text-white/40 ml-1">filtra solo nodos KMZ</span>
            )}
          </div>
          {showEmpty && (
            <div className="px-4 py-6 text-center text-white/40 text-xs">Ningún resultado para «{q}».</div>
          )}
          {!showEmpty && loading && !data && (
            <div className="px-4 py-6 text-center text-white/40 text-xs">Buscando…</div>
          )}
          {data && flat.length > 0 && (
            <ul className="py-1">
              {['site', 'node', 'negotiation', 'incident'].map((k) => {
                const items = flat.filter((r) => r.group === k);
                if (!items.length) return null;
                const meta = KIND_META[k];
                const Icon = meta.icon;
                return (
                  <li key={k}>
                    <div className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-white/40 flex items-center gap-2 sticky top-0 bg-[#0d0d0d]">
                      <Icon size={11} className={meta.color}/> {meta.label}
                      <span className="text-white/30">· {items.length}</span>
                    </div>
                    {items.map((r) => {
                      const idx = flat.indexOf(r);
                      const active = idx === highlight;
                      return (
                        <button key={`${r.kind}-${r.id}`} onClick={() => jump(r)} onMouseEnter={() => setHighlight(idx)}
                          data-testid={`gs-result-${r.kind}-${r.id}`}
                          className={`w-full text-left px-3 py-2 flex items-start gap-2.5 border-t border-white/5 ${
                            active ? 'bg-[#A3FF12]/10' : 'hover:bg-white/5'
                          }`}>
                          <Icon size={13} className={`${meta.color} mt-0.5 flex-shrink-0`}/>
                          <div className="min-w-0 flex-1">
                            <div className="text-white text-xs font-bold truncate">{r.title}</div>
                            <div className="text-white/50 text-[10px] truncate">{r.subtitle}</div>
                          </div>
                        </button>
                      );
                    })}
                  </li>
                );
              })}
            </ul>
          )}

          {addresses.length > 0 && (
            <div className="border-t border-white/10" data-testid="gs-addresses">
              <div className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-white/40 flex items-center gap-2 sticky top-0 bg-[#0d0d0d]">
                <Navigation size={11} className="text-blue-300"/> Direcciones
                <span className="text-white/30">· {addresses.length}</span>
              </div>
              {addresses.map((a, i) => (
                <button key={`addr-${i}`} onClick={() => jumpAddress(a)} data-testid={`gs-address-${i}`}
                  className="w-full text-left px-3 py-2 flex items-start gap-2.5 border-t border-white/5 hover:bg-white/5">
                  <Navigation size={13} className="text-blue-300 mt-0.5 flex-shrink-0"/>
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-xs font-bold truncate">{a.display_name.split(',')[0]}</div>
                    <div className="text-white/50 text-[10px] truncate">{a.display_name}</div>
                  </div>
                  <span className="text-white/30 text-[10px] flex-shrink-0">
                    {a.city && `${a.city}`}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
